import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { CheckmarkCircle32Filled } from "@fluentui/react-icons";
import {
  BIG_COPY,
  GEB_HINWEISE,
  GEB_EMPFEHLUNGEN,
  KFZ_HINWEISE,
  KFZ_EMPFEHLUNGEN,
  SON_HINWEISE,
  type HinweisBlock,
} from "../copy";
import type { BigAnswers } from "../types";
import { PhotoUploadImmediate } from "../PhotoUpload";
import { uploadSchadenmeldungFile } from "../api";
import { FONT_ACCENT, FONT_BODY } from "../../schadenmeldung/theme";

interface Props {
  answers: BigAnswers;
  referenceCode: string;
  onRestart: () => void;
}

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "14px",
    paddingTop: "20px",
  },
  bubble: {
    width: "84px",
    height: "84px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--sm-accent-soft)",
    color: "var(--sm-accent)",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("3px", "solid", "var(--sm-accent)"),
  },
  title: {
    margin: 0,
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
    fontWeight: 500,
  },
  body: { margin: 0, maxWidth: "520px", color: "#6c6171", fontFamily: FONT_BODY },
  refBox: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "22px",
    paddingRight: "22px",
    backgroundColor: "#fff",
    ...shorthands.border("1.5px", "dashed", "var(--sm-accent)"),
    ...shorthands.borderRadius("12px"),
  },
  refLabel: {
    color: "var(--sm-accent)",
    fontFamily: FONT_BODY,
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  refValue: {
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontSize: "1.8rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
  },
  notice: {
    width: "100%",
    textAlign: "left",
    marginTop: "20px",
  },
  noticeTitle: {
    margin: "0 0 8px 0",
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontSize: "1.2rem",
    fontWeight: 500,
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#5c5364",
    fontFamily: FONT_BODY,
    fontSize: "0.9rem",
    lineHeight: 1.55,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  details: {
    width: "100%",
    textAlign: "left",
    backgroundColor: "#fff",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("10px"),
    marginBottom: "8px",
  },
  summary: {
    cursor: "pointer",
    fontFamily: FONT_BODY,
    fontWeight: 600,
    color: "var(--sm-primary-deep)",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
  },
  detailsBody: { paddingBottom: "12px", paddingLeft: "14px", paddingRight: "14px" },
  team: {
    marginTop: "16px",
    fontFamily: FONT_ACCENT,
    fontSize: "1.05rem",
    color: "var(--sm-primary-deep)",
  },
  restart: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "var(--sm-primary)",
    fontFamily: FONT_BODY,
    fontWeight: 600,
    marginTop: "18px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.border("1.5px", "solid", "var(--sm-primary)"),
    ...shorthands.borderRadius("10px"),
    ":hover": { backgroundColor: "var(--sm-accent-soft)" },
  },
});

export default function DoneStep({ answers, referenceCode, onRestart }: Props) {
  const styles = useStyles();
  const C = BIG_COPY.done;
  const hinweise =
    answers.damageType === "kfz"
      ? KFZ_HINWEISE
      : answers.damageType === "gebaeude"
        ? GEB_HINWEISE
        : SON_HINWEISE;
  const empfehlungen: HinweisBlock[] =
    answers.damageType === "kfz"
      ? KFZ_EMPFEHLUNGEN
      : answers.damageType === "gebaeude"
        ? GEB_EMPFEHLUNGEN
        : [];

  return (
    <div className={styles.wrap}>
      <div className={styles.bubble} aria-hidden>
        <CheckmarkCircle32Filled style={{ fontSize: 50 }} />
      </div>
      <h1 className={styles.title}>{C.title}</h1>
      <p className={styles.body}>{C.body}</p>
      <div className={styles.refBox}>
        <span className={styles.refLabel}>{C.referenceLabel}</span>
        <span className={styles.refValue}>{referenceCode}</span>
      </div>

      <div className={styles.notice}>
        <h2 className={styles.noticeTitle}>Fotos oder Unterlagen hochladen</h2>
        <p className={styles.body} style={{ textAlign: "left", marginBottom: 10 }}>
          Sie können jetzt noch Fotos oder Unterlagen zu Ihrer Meldung hinzufügen.
        </p>
        <PhotoUploadImmediate onUpload={(file) => uploadSchadenmeldungFile(referenceCode, file).then(() => undefined)} />
      </div>

      {hinweise.length > 0 && (
        <div className={styles.notice}>
          <h2 className={styles.noticeTitle}>Wichtige Hinweise für Sie</h2>
          <ul className={styles.list}>
            {hinweise.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </div>
      )}

      {empfehlungen.length > 0 && (
        <div className={styles.notice}>
          <h2 className={styles.noticeTitle}>Empfehlungen je Schadenart</h2>
          {empfehlungen.map((block) => (
            <details key={block.title} className={styles.details}>
              <summary className={styles.summary}>{block.title}</summary>
              <div className={styles.detailsBody}>
                <ul className={styles.list}>
                  {block.items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              </div>
            </details>
          ))}
        </div>
      )}

      <div className={styles.notice}>
        <h2 className={styles.noticeTitle}>{C.naechsteTitle}</h2>
        <ul className={styles.list}>
          {C.naechste.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>

      <div className={styles.team}>{C.team}</div>
      <button type="button" className={styles.restart} onClick={onRestart}>
        {BIG_COPY.nav.again}
      </button>
    </div>
  );
}
